import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthState } from "@/hooks/useAuthState";

export interface EntitlementItem {
  enabled: boolean;
  value_type: "included" | "not_included" | "limited" | "limit" | "extra";
  limit: number | null;
  status: "activo" | "proximamente" | "oculto";
  source: string | null;
}

export interface Entitlements {
  v2_enabled: boolean;
  /** null = sin plan (lista de acceso anticipado); nunca «sin_plan». */
  plan_key: string | null;
  plan_source: string;
  is_admin: boolean;
  legacy_panel: boolean;
  items: Record<string, EntitlementItem>;
}

export const entitlementsQueryKey = (userId: string | null) => ["entitlements", userId] as const;

/** Permisos efectivos calculados en servidor (get_entitlements). */
export function useEntitlements() {
  const { user, isReady } = useAuthState();
  const userId = user?.id ?? null;
  const query = useQuery({
    queryKey: entitlementsQueryKey(userId),
    enabled: isReady && !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_entitlements", { _user_id: userId });
      if (error) throw error;
      return data as Entitlements;
    },
  });
  return { ...query, entitlements: query.data ?? null };
}

/** Acceso a un elemento concreto del catálogo; null mientras carga o con la lógica antigua. */
export function useEntitlement(catalogKey: string) {
  const { entitlements, isLoading } = useEntitlements();
  const item = entitlements?.items?.[catalogKey] ?? null;
  return { v2: !!entitlements?.v2_enabled, item, enabled: !!item?.enabled, loading: isLoading };
}
